import { StringCodec } from "nats.ws";

import { isNonPrimitive } from "@/utils/type";

/**
 * Decodes the given NATS response data, and parses the JSON it contains.
 * This handles non-standard JSON numbers which are output by Python's json.dumps
 * (Infinity, -Infinity, NaN).
 */
export function decodeJson<T = unknown>(byteArray: Uint8Array): T {
  const codec = StringCodec();
  const stringResponse = codec.decode(byteArray);
  const sanitizedResponse = sanitizeNonJsonNumbers(stringResponse);
  return JSON.parse(sanitizedResponse, infinityReviver) as T;
}

/**
 * Sanitizes a JSON string by converting non-standard numeric values
 * (Infinity, -Infinity, NaN) into sentinel objects that can be revived later.
 *
 * This preprocessor scans the JSON string character-by-character, tracking
 * whether we're inside a string literal or not. When outside strings, it
 * looks for unquoted Infinity/-Infinity/NaN at value positions and replaces
 * them with `{"$number":"..."}` sentinel objects.
 */
function sanitizeNonJsonNumbers(json: string): string {
  let out = "";
  let i = 0;
  let inString = false;
  let escape = false;

  // Copy in the input, one character at a time
  while (i < json.length) {
    const c = json[i];

    // Handle characters if we're already inside a string
    if (inString) {
      out += c;

      if (escape) {
        escape = false;
      } else if (c === "\\") {
        escape = true;
      } else if (c === '"') {
        inString = false;
      }

      i++;
      continue;
    }

    // Register the start of a string
    if (c === '"') {
      inString = true;
      out += c;
      i++;
      continue;
    }

    // From here on, we only want to consider value-start positions in JSON (outside strings)
    if (c !== ":" && c !== "[" && c !== ",") {
      out += c;
      i++;
      continue;
    }

    // Copy structural character (:, [, ,)
    out += c;
    i++;

    // Copy whitespace
    while (json[i] === " " || json[i] === "\n" || json[i] === "\r" || json[i] === "\t") {
      out += json[i];
      i++;
    }

    // If value is a string, do nothing (this will be copied in the next iteration of the loop)
    if (json[i] === '"') {
      continue;
    }

    // Check for each of the 3 non-standard JSON tokens

    // -Infinity
    if (json.startsWith("-Infinity", i)) {
      out += '{"$number":"-Infinity"}';
      i += 9;
      continue;
    }

    // Infinity
    if (json.startsWith("Infinity", i)) {
      out += '{"$number":"Infinity"}';
      i += 8;
      continue;
    }

    // NaN
    if (json.startsWith("NaN", i)) {
      out += '{"$number":"NaN"}';
      i += 3;
      continue;
    }

    // If it doesn't match, the rest of the value will be copied in successive iterations of the loop
  }

  return out;
}

/**
 * JSON reviver that restores numeric sentinel objects for non-standard JSON numbers.
 * Converts objects like {"$number":"Infinity"}, {"$number":"-Infinity"}, {"$number":"NaN"}
 * back into their JavaScript numeric values: Infinity, -Infinity, or NaN.
 *
 * Leaves normal strings (e.g., "Infinity") unchanged.
 *
 * @param _ - Key (ignored)
 * @param value - Value to potentially transform
 * @returns - Restored numeric value or original value
 */
function infinityReviver(_: string, value: unknown): unknown {
  if (isNumberSentinel(value)) {
    switch (value.$number) {
      case "Infinity":
        return Infinity;
      case "-Infinity":
        return -Infinity;
      case "NaN":
        return NaN;
    }
  }
  return value;
}

/**
 * Type guard to check if a value is a NumberSentinel object.
 */
function isNumberSentinel(value: unknown): value is NumberSentinel {
  return isNonPrimitive(value) && "$number" in value && typeof value.$number === "string";
}

interface NumberSentinel {
  $number: "Infinity" | "-Infinity" | "NaN";
}
